<script lang="ts">
  import { defineComponent, PropType } from 'vue'

  export default defineComponent({
    name: 'BaseNotification',
    props: {
      type: {
        type: String as PropType<'success' | 'info' | 'warning' | 'error'>,
        default: 'error',
      },
      withCrossButton: {
        type: Boolean,
        default: true,
      },
      isFixed: {
        type: Boolean,
        default: false,
      },
      withEmit: {
        type: Boolean,
        default: false,
      },
      innerClass: {
        type: String,
        default: '',
      },
      withProgressBar: {
        type: Boolean,
        default: false,
      },
      isFullWidth: {
        type: Boolean,
        default: false,
      },
      role: {
        type: String,
        default: 'alert',
      },
      // Set to promote the message to a heading + body pair. The leading icon and the close
      // button then align to the first line instead of the vertical centre.
      title: {
        type: String,
        default: '',
      },
    },
    emits: ['close'],
    data() {
      return {
        isShow: true,
      }
    },
    computed: {
      getCrossColor() {
        switch (this.type) {
          case 'success':
            return {
              bg: '#EBF8F6',
              color: '#07A287',
            }
          case 'info':
            return {
              bg: '#eaf6f9',
              color: '#50869c',
            }
          case 'warning':
            return {
              bg: '#FFF8F1',
              color: '#C15216',
            }
          case 'error':
            return {
              bg: '#FFF1F4',
              color: '#A94442',
            }
          default:
            return {
              bg: '#EBF8F6',
              color: '#07A287',
            }
        }
      },
    },
    methods: {
      closeNotification() {
        if (this.withEmit) {
          this.$emit('close')
        } else {
          this.isShow = !this.isShow
        }
      },
    },
  })
</script>

<template>
  <div :class="{ fixed: isFixed }">
    <div
      v-if="isShow"
      class="notification"
      :class="{
        'notification--success': type === 'success',
        'notification--information': type === 'info',
        'notification--warning': type === 'warning',
        'notification--error': type === 'error',
        [innerClass]: innerClass,
        'w-100 d-flex justify-center': isFullWidth,
        'notification--with-title': title,
      }"
    >
      <slot name="icon"></slot>

      <div v-if="title" class="notification__content">
        <p class="notification__title">{{ title }}</p>
        <p class="notification__text"><slot></slot></p>
      </div>

      <slot v-else></slot>

      <div v-if="withCrossButton" class="notification__close">
        <BaseCircleProgressBar
          v-if="withProgressBar"
          :progress-bar-time="100"
          :progress-bar-color="getCrossColor.color"
          :progress-bar-bg-color="getCrossColor.bg"
          @timerEnd="closeNotification"
        >
          <BaseIcon
            tabindex="1"
            class="cursor-pointer notification__close-button"
            data-test-id="notification-close"
            icon-name="cross-inline-icon.svg"
            icon-color="black"
            width="12px"
            height="12px"
            @click="closeNotification"
            @keydown.esc.enter="closeNotification"
          />
        </BaseCircleProgressBar>
        <BaseIcon
          v-else
          class="cursor-pointer notification__close-button"
          tabindex="1"
          data-test-id="notification-close"
          icon-name="cross-inline-icon.svg"
          icon-color="black"
          width="18px"
          height="18px"
          @click="closeNotification"
          @keydown.esc.enter="closeNotification"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .notification__content {
    flex: 1 1 auto;
    min-width: 0;
  }

  .notification__title {
    margin: 0 0 var(--bf-sp-1);
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm_5);
    font-weight: var(--bf-fw-semibold);
    line-height: 1.35;
    letter-spacing: -0.005em;
    color: var(--bf-info-accent);
  }

  .notification__text {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--bf-info-text);
    text-wrap: pretty;
  }

  .fixed {
    position: fixed;
    top: 100px;
    z-index: 100;
  }
  /* Layout and type live on the block, not on utility classes in the class list. */
  .notification {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0;
    padding: 18px;
    border-radius: 8px;
    position: relative;
    line-height: 20px;

    &--success {
      background-color: #ebf8f6;
      color: var(--color-alert-text-green);
    }
    &--information {
      background-color: var(--bf-info-bg);
      border: 1px solid var(--bf-info-border);
      border-left: 4px solid var(--bf-info-accent);
      font-size: 13.5px;
      line-height: 1.55;
      color: var(--bf-info-text);
    }

    &--information :deep(a) {
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    /* ---------- Titled variant ----------
       Same info surface; the leading icon and close button align to the first line instead of
       the vertical centre, and the message becomes a heading + body pair. */
    &--with-title {
      align-items: flex-start;
    }

    &--information.notification--with-title :deep(.icon[data-icon='icon-info.svg']),
    &--information.notification--with-title &__close-button {
      margin-top: 1px;
    }

    /* Close (X) restyled to the design's muted-slate icon with a teal hover pill.
       Scoped to the info variant so the banner's leading icon is untouched. */
    &--information &__close-button {
      padding: 4px;
      border-radius: var(--bf-r-md);
      box-sizing: content-box;
      transition:
        background var(--bf-dur-fast) var(--bf-ease),
        color var(--bf-dur-fast) var(--bf-ease);
    }

    &--information &__close-button :deep(i) {
      background-color: var(--bf-info-close);
      transition: background-color var(--bf-dur-fast) var(--bf-ease);
    }

    &--information &__close-button:hover {
      background: var(--bf-info-close-hover-bg);
    }

    &--information &__close-button:hover :deep(i) {
      background-color: var(--bf-info-text);
    }

    &--warning {
      background-color: #fff8f1;
      color: var(--color-alert-text-orange);
    }
    &--error {
      background-color: #fff1f4;
      color: var(--color-alert-text-red);
    }

    /* Never let the flex row squeeze the close control. */
    &__close {
      flex: none;
      margin-left: 36px;
    }
  }
</style>
